import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../buttons/CustomButton';
import { ButtonTypes } from '../../../services/exports/Interfaces';
import { PAYMENT_METHODS } from '../../../services/exports/Constants';
import useTheme from '../../../hooks/ui/useTheme';
import useDialogFocus from '../../../hooks/ui/useDialogFocus';
import paymentLog from '../../../services/helpers/PaymentLogger';
import type { SpreedlyStripeApmSheetState } from '../../../hooks/payment/useSpreedlyOffsitePayment';
import withTabsPaymentElement from './withTabsPaymentElement';

export const SPREEDLY_STRIPE_ELEMENT_EL_ID = 'spreedly-stripe-payment-element';

interface Props {
  sheet: SpreedlyStripeApmSheetState;
  onCancel: () => void;
  onRedirecting: () => void;
}

/**
 * Stripe's Payment Element for a pending Stripe APM purchase, mounted through Spreedly's iFrame.
 * For iDEAL it is a single confirm step, for BLIK a code field; either way the customer's click
 * on Continue is what sends them to their bank or app, and Stripe then returns them to us through
 * Spreedly.
 */
export default function SpreedlyStripeApmSheet(props: Props) {
  const { sheet, onCancel, onRedirecting } = props;
  const theme = useTheme();
  const { t } = useTranslation(null, { keyPrefix: 'Components:Payment:Spreedly:SpreedlyStripeApmSheet' });
  const handleRef = useRef<Spreedly.StripePaymentElementHandle | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDialogFocus(panelRef);

  const methodName =
    PAYMENT_METHODS().find((method) => method.payment_method_id === sheet.apmType)?.display_name ?? sheet.apmType;

  useEffect(() => {
    const appearance = {
      theme: theme.isDarkMode ? 'night' : 'stripe',
      variables: {
        colorPrimary: theme.color.brand.text.primary,
        colorText: theme.color.brand.text.primary,
      },
    };
    const created = withTabsPaymentElement(() =>
      window.Spreedly?.createStripePaymentElement?.({
        publishableKey: sheet.publishableKey,
        clientSecret: sheet.clientSecret,
        paymentElement: `#${SPREEDLY_STRIPE_ELEMENT_EL_ID}`,
        transactionToken: sheet.transactionToken,
        // Spreedly's bundle reads only the misspelt `appeareance` and hands it to `stripe.elements()`
        // under that key, which Stripe ignores; withTabsPaymentElement renames it to `appearance`. The
        // correct spelling rides along for the day Spreedly reads it.
        appeareance: appearance,
        appearance,
      }),
    );

    if (!created || 'error' in created) {
      paymentLog.error('spreedly', 'offsite_payment_element_failed', {
        payment_method: sheet.apmType,
        message: created && 'error' in created ? created.error : 'sdk_missing',
      });
      setError(t('unavailable'));

      return;
    }

    handleRef.current = created;

    // Spreedly keeps Stripe's element to itself (the handle is confirmPayment only), so the
    // mount point is emptied instead: a sheet reopened for a new attempt would otherwise stack a
    // second element under the first.
    return () => {
      handleRef.current = null;
      document.getElementById(SPREEDLY_STRIPE_ELEMENT_EL_ID)?.replaceChildren();
    };
  }, [sheet.transactionToken]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) {
        onCancel();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, submitting]);

  const onContinue = async () => {
    if (!handleRef.current || submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await handleRef.current.confirmPayment();

      if (result?.error) {
        // Stripe answers in-page only when it can refuse without the bank: an incomplete field,
        // an expired BLIK code. The customer fixes it here and continues again.
        paymentLog.warn('spreedly', 'offsite_confirm_refused', {
          payment_method: sheet.apmType,
          type: result.error.type,
          message: result.error.message,
        });
        setError(result.error.message ?? t('unavailable'));
        setSubmitting(false);

        return;
      }

      onRedirecting();
    } catch (confirmError) {
      paymentLog.error('spreedly', 'offsite_confirm_failed', {
        payment_method: sheet.apmType,
        message: confirmError instanceof Error ? confirmError.message : undefined,
      });
      setError(t('unavailable'));
      setSubmitting(false);
    }
  };

  return (
    <div className={overlay} data-test="spreedly-apm-sheet">
      <div
        ref={panelRef}
        tabIndex={-1}
        className={panel}
        role="dialog"
        aria-modal="true"
        aria-label={t('title', { method: methodName })}
      >
        <p className={title}>{t('title', { method: methodName })}</p>
        <p className={hint}>{t('hint')}</p>
        {/* mp-block: Stripe's element can carry a BLIK code or bank details. */}
        <div id={SPREEDLY_STRIPE_ELEMENT_EL_ID} className="mp-block min-h-[48px]" />
        {error && (
          <p className={errorText} role="alert" data-test="spreedly-apm-sheet-error">
            {error}
          </p>
        )}
        <CustomButton
          buttonType={ButtonTypes.primary}
          title={t('continue')}
          onClick={onContinue}
          loading={submitting}
          disabled={!handleRef.current && !!error}
          className="mt-md w-full"
          testId="spreedly-apm-continue"
        />
        <button
          type="button"
          className={cancelButton}
          onClick={onCancel}
          disabled={submitting}
          data-test="spreedly-apm-cancel"
        >
          {t('cancel')}
        </button>
      </div>
    </div>
  );
}

const overlay = 'fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-md';
const panel = 'bg-brand-background-primary rounded-lg p-md w-full max-w-[480px]';
const title = 'text-md font-semibold mb-xs text-brand-text-primary dark:text-white';
const hint = 'text-sm mb-sm text-brand-text-primary opacity-80 dark:text-white';
const errorText = 'mt-sm text-sm text-danger';
const cancelButton =
  'mt-sm block w-full text-center text-sm font-medium underline underline-offset-2 text-brand-text-primary hover:opacity-80 disabled:opacity-50 dark:text-white';
